import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as ResolvedIcon,
  Schedule as PendingIcon,
  Close as CloseIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import {
  getAllContactMessages,
  updateContactMessageStatus,
  ContactMessage,
} from '../../services/contactService';

export const AdminContacts: React.FC = () => {
  const { theme } = useTheme();
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await getAllContactMessages();
      setMessages(data);
    } catch (err) {
      console.error('Erro ao carregar mensagens:', err);
      setError('Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (messageId: string, status: 'pending' | 'resolved') => {
    try {
      setUpdating(true);
      await updateContactMessageStatus(messageId, status);
      await loadMessages();
      setSelectedMessage(null);
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
      setError('Erro ao atualizar status');
    } finally {
      setUpdating(false);
    }
  };

  const pendingMessages = messages.filter((m) => m.status === 'pending');
  const resolvedMessages = messages.filter((m) => m.status === 'resolved');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress aria-label="Carregando" sx={{ color: theme.colors.primary }} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Chip
          icon={<PendingIcon />}
          label={`${pendingMessages.length} Pendentes`}
          color="warning"
          variant="outlined"
        />
        <Chip
          icon={<ResolvedIcon />}
          label={`${resolvedMessages.length} Resolvidas`}
          color="success"
          variant="outlined"
        />
      </Box>

      {/* Mensagens Pendentes */}
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: theme.colors.text.primary }}>
        Mensagens Pendentes
      </Typography>

      {pendingMessages.length === 0 ? (
        <Card sx={{ p: 3, mb: 4, textAlign: 'center', backgroundColor: theme.colors.card }}>
          <Typography color="text.secondary">Nenhuma mensagem pendente</Typography>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
          {pendingMessages.map((message) => (
            <Card
              key={message.id}
              component="button"
              type="button"
              aria-label={`Abrir mensagem: ${message.subject}`}
              sx={{
                p: 2,
                width: '100%',
                textAlign: 'left',
                font: 'inherit',
                color: 'inherit',
                display: 'block',
                backgroundColor: theme.colors.card,
                border: `1px solid ${theme.colors.border}`,
                cursor: 'pointer',
                '&:hover': {
                  borderColor: theme.colors.primary,
                },
              }}
              onClick={() => setSelectedMessage(message)}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.colors.text.primary }}>
                    {message.subject}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    De: {message.userName} ({message.userEmail})
                  </Typography>
                </Box>
                <Chip label="Pendente" color="warning" size="small" />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: theme.colors.text.secondary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {message.message}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {message.createdAt?.toLocaleDateString('pt-BR')} às{' '}
                {message.createdAt?.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Card>
          ))}
        </Box>
      )}

      {/* Mensagens Resolvidas */}
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: theme.colors.text.primary }}>
        Mensagens Resolvidas
      </Typography>

      {resolvedMessages.length === 0 ? (
        <Card sx={{ p: 3, textAlign: 'center', backgroundColor: theme.colors.card }}>
          <Typography color="text.secondary">Nenhuma mensagem resolvida</Typography>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {resolvedMessages.map((message) => (
            <Card
              key={message.id}
              component="button"
              type="button"
              aria-label={`Abrir mensagem resolvida: ${message.subject}`}
              sx={{
                p: 2,
                width: '100%',
                textAlign: 'left',
                font: 'inherit',
                color: 'inherit',
                display: 'block',
                backgroundColor: theme.colors.card,
                border: `1px solid ${theme.colors.border}`,
                cursor: 'pointer',
                opacity: 0.7,
                '&:hover': {
                  opacity: 1,
                  borderColor: theme.colors.primary,
                },
              }}
              onClick={() => setSelectedMessage(message)}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.colors.text.primary }}>
                    {message.subject}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    De: {message.userName} ({message.userEmail})
                  </Typography>
                </Box>
                <Chip label="Resolvida" color="success" size="small" />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: theme.colors.text.secondary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {message.message}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Resolvida em {message.resolvedAt?.toLocaleDateString('pt-BR')}
              </Typography>
            </Card>
          ))}
        </Box>
      )}

      {/* Dialog de Detalhes */}
      <Dialog
        aria-labelledby="admincontacts-dlg1-title"
        open={!!selectedMessage}
        onClose={() => setSelectedMessage(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.radii.large,
            backgroundColor: theme.colors.background,
          },
        }}
      >
        {selectedMessage && (
          <>
            <DialogTitle id="admincontacts-dlg1-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {selectedMessage.subject}
              </Typography>
              <IconButton onClick={() => setSelectedMessage(null)} aria-label="Fechar">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <strong>De:</strong> {selectedMessage.userName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <strong>Email:</strong> {selectedMessage.userEmail}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <strong>Data:</strong> {selectedMessage.createdAt?.toLocaleDateString('pt-BR')} às{' '}
                  {selectedMessage.createdAt?.toLocaleTimeString('pt-BR')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Status:</strong>{' '}
                  <Chip
                    label={selectedMessage.status === 'pending' ? 'Pendente' : 'Resolvida'}
                    color={selectedMessage.status === 'pending' ? 'warning' : 'success'}
                    size="small"
                  />
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: theme.colors.card,
                  borderRadius: theme.radii.medium,
                  border: `1px solid ${theme.colors.border}`,
                }}
              >
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedMessage.message}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              <Button
                href={`mailto:${selectedMessage.userEmail}?subject=Re: ${selectedMessage.subject}`}
                startIcon={<EmailIcon />}
                sx={{ color: theme.colors.primary }}
              >
                Responder por Email
              </Button>
              {selectedMessage.status === 'pending' ? (
                <Button
                  onClick={() => handleStatusChange(selectedMessage.id!, 'resolved')}
                  variant="contained"
                  disabled={updating}
                  sx={{
                    backgroundColor: theme.colors.primary,
                    '&:hover': { backgroundColor: theme.colors.primary, opacity: 0.9 },
                  }}
                >
                  {updating ? <CircularProgress aria-label="Carregando" size={20} /> : 'Marcar como Resolvida'}
                </Button>
              ) : (
                <Button
                  onClick={() => handleStatusChange(selectedMessage.id!, 'pending')}
                  variant="outlined"
                  disabled={updating}
                >
                  {updating ? <CircularProgress aria-label="Carregando" size={20} /> : 'Marcar como Pendente'}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};
